import { useNavigation } from '@react-navigation/native';
import React, { useMemo, useState } from 'react';
import { useErrorHandler } from 'react-error-boundary';
import styled from 'styled-components/native';
import { ScrollView } from 'react-native';

import { Color } from '~/components/color';
import { Markdown } from '~/components/markdown';
import { Text } from '~/components/text';
import { useAppointmentsSelection } from '~/contexts/appointments-selection';
import { AppError } from '~/error';
import { useAppointmentsContextDescription } from '~/hooks/appointments-context-description/appointments-context-description';

import { Button } from '~/components/button';
import { useIntl } from '~/contexts/intl';

import { AppointmentsNavigationProp } from './appointments';

const MAX_DESCRIPTION_CHARACTERS = 120;

const MAX_EXPANDED_DESCRIPTION_HEIGHT = 300;

const AppointmentContextDescription = styled.View`
    margin-bottom: 24px;
`;

export const AppointmentsContentContextDescription = () => {
    const { calendarReservationAccessId, region, location, filters } = useAppointmentsSelection();
    const { canGoBack, goBack, navigate } = useNavigation<AppointmentsNavigationProp>(); // TODO: AppointmentsNavigationProp is defined in two places now
    const handleError = useErrorHandler();

    const { contextDescription, error } = useAppointmentsContextDescription(
        calendarReservationAccessId,
        filters,
        region,
        location
    );

    if (error) {
        handleError(
            new AppError(error, 'appointments.error.not-available', {
                name: 'error-overlay.go_back',
                onClose: () => (canGoBack() ? goBack() : navigate('profile'))
            })
        );
    }

    const { formatMessage } = useIntl();

    const [isExpanded, setIsExpanded] = useState(false);

    const { displayText, shouldTruncate } = useMemo(() => {
        const full = contextDescription ?? '';
        const should = full.length > MAX_DESCRIPTION_CHARACTERS && !isExpanded;
        const text = should ? `${full.slice(0, MAX_DESCRIPTION_CHARACTERS)}...` : full;
        return { displayText: text, shouldTruncate: full.length > MAX_DESCRIPTION_CHARACTERS };
    }, [contextDescription, isExpanded]);

    return (
        <AppointmentContextDescription>
            {contextDescription ? (
                <>
                    {isExpanded ? (
                        <ScrollView style={{ maxHeight: MAX_EXPANDED_DESCRIPTION_HEIGHT }} showsVerticalScrollIndicator>
                            <Markdown
                                styles={{
                                    heading1: { ...Text.SUBTITLE_1_STYLES, color: Color.SMOOTHIE },
                                    heading2: { ...Text.PARAGRAPH_1_STYLES, marginTop: 8 },
                                    heading3: Text.SUBTITLE_1_STYLES,
                                    strong: Text.PARAGRAPH_2_STYLES
                                }}
                            >
                                {contextDescription}
                            </Markdown>
                        </ScrollView>
                    ) : (
                        <Markdown
                            styles={{
                                heading1: { ...Text.SUBTITLE_1_STYLES, color: Color.SMOOTHIE },
                                heading2: { ...Text.PARAGRAPH_1_STYLES, marginTop: 8 },
                                heading3: Text.SUBTITLE_1_STYLES,
                                strong: Text.PARAGRAPH_2_STYLES
                            }}
                        >
                            {displayText}
                        </Markdown>
                    )}
                    {shouldTruncate ? (
                        <Button
                            type="text"
                            size="small"
                            alignCenter={false}
                            label={isExpanded ? formatMessage('showLess') : formatMessage('showMore')}
                            onPress={() => setIsExpanded(prev => !prev)}
                        />
                    ) : null}
                </>
            ) : null}
        </AppointmentContextDescription>
    );
};
